import { useState, useEffect, useRef, useCallback } from 'react';

const generateOrder = () => {
  const sides = ['B', 'S'] as const;
  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
  const side = sides[Math.floor(Math.random() * 2)];
  const qty = (Math.floor(Math.random() * 80) + 1) * 100;
  const px = (183 + Math.random() * 4).toFixed(2);
  const aggr = Math.random() > 0.5;
  return { time, side, qty: qty.toLocaleString(), px, aggr };
};

const strategyList = [
  { id: 'α-07', name: '多因子Alpha', signal: 'LONG' as const, status: 'green' as const, freq: '125ms', confidence: 94.7, allocation: '18.2%', dailyPnL: '+$42,180', monthlyPnL: '+$847K', riskContrib: '21.4%', sharpe: 2.41 },
  { id: 'β-12', name: '统计套利组', signal: 'ENTRY' as const, status: 'green' as const, freq: '89ms', confidence: 88.3, allocation: '14.6%', dailyPnL: '+$28,450', monthlyPnL: '+$592K', riskContrib: '16.8%', sharpe: 2.18 },
  { id: 'γ-03', name: 'CTA趋势', signal: 'HOLD' as const, status: 'yellow' as const, freq: '210ms', confidence: 72.1, allocation: '22.1%', dailyPnL: '-$3,210', monthlyPnL: '+$124K', riskContrib: '24.7%', sharpe: 1.63 },
  { id: 'δ-09', name: '波动率曲面', signal: 'WAIT' as const, status: 'red' as const, freq: '340ms', confidence: 45.8, allocation: '8.4%', dailyPnL: '-$12,840', monthlyPnL: '-$86K', riskContrib: '9.2%', sharpe: 0.78 },
  { id: 'ε-04', name: '事件驱动', signal: 'EXIT' as const, status: 'red' as const, freq: '156ms', confidence: 31.2, allocation: '5.6%', dailyPnL: '-$8,920', monthlyPnL: '-$210K', riskContrib: '7.4%', sharpe: 0.52 },
  { id: 'ζ-11', name: '高频做市', signal: 'LONG' as const, status: 'green' as const, freq: '12ms', confidence: 97.1, allocation: '31.1%', dailyPnL: '+$67,300', monthlyPnL: '+$1.42M', riskContrib: '20.5%', sharpe: 3.15 },
];

const tradeLogs = [
  '[14:32:21] α-07 > 建仓 HKEX:0700 500sh @ $388.20',
  '[14:32:19] β-12 > 配对平仓 AAPL/MSFT spread +$0.84',
  '[14:32:18] RISK > VaR(95%) 下调 0.12% → 1.84%',
  '[14:32:17] γ-03 > 调仓 GOLD 期货 +120ct',
  '[14:32:15] δ-09 > 隐含波动率曲面偏离 2.3σ',
  '[14:32:14] α-07 > 止损触发 TSLA -$1,240',
  '[14:32:12] SYS  > 风控限额检查通过 · 无告警',
  '[14:32:10] β-12 > 新增对冲仓位 NQ ·  Delta中性',
  '[14:32:08] ε-04 > 事件窗口触发 · Earnings Q2',
  '[14:32:06] ζ-11 > 报价深度更新 · 42档/ side',
  '[14:32:04] RISK > 集中度监控 · 单票上限 8.2%',
  '[14:32:02] α-07 > 信号置信度 94.7% · 执行中',
];

const pnlPoints = [42, 38, 45, 41, 52, 48, 55, 51, 58, 62, 59, 65, 61, 68, 72, 69, 75, 78, 74, 80, 85, 82, 88, 91, 87, 93, 96, 92, 98, 102, 99, 105, 108, 104, 110, 113, 109, 115, 118, 114, 120, 123, 119, 125, 128, 124, 130, 133, 129, 135];

// ── K-line data generators per period ──
type KlinePeriod = '1D' | '1W' | '1M' | '3M';

const genCandles = (period: KlinePeriod): number[] => {
  const seeds: Record<KlinePeriod, number[]> = {
    '1D': [50,40,65,45,75,55,90,60,80,70,48,65,40,75,50,85,55,70,62,52,80,60,90,55,75,68,45,80,55,72,62,50,85,58,70,65,55,78,60,82],
    '1W': [62,55,78,50,88,68,95,58,72,75,45,70,52,82,62,90,58,74,68,48,85,55,92,60,78,72,52,84,58,76,65,50,88,62,80,70,55,82,65,86],
    '1M': [75,65,88,55,95,72,102,62,80,85,50,78,58,90,68,98,60,82,72,52,92,58,100,64,85,78,55,90,62,84,70,54,95,65,88,76,58,90,68,94],
    '3M': [88,78,102,68,108,82,115,72,92,98,58,88,65,102,75,110,68,94,82,60,105,65,115,72,98,88,62,104,68,96,78,60,108,72,102,85,62,104,75,110],
  };
  return seeds[period];
};

const periodLabels: { key: KlinePeriod; label: string }[] = [
  { key: '1D', label: '1D' },
  { key: '1W', label: '1W' },
  { key: '1M', label: '1M' },
  { key: '3M', label: '3M' },
];

export default function TerminalPanel() {
  const [orders, setOrders] = useState(() =>
    Array.from({ length: 8 }, () => generateOrder())
  );
  const [activeSignal, setActiveSignal] = useState(0);
  const [expandedSignal, setExpandedSignal] = useState<string | null>(null);
  const [hsiPrice, setHsiPrice] = useState(18293.51);
  const [lastTickUp, setLastTickUp] = useState(true);
  const [logIndex, setLogIndex] = useState(0);
  const [pnlVal, setPnlVal] = useState('+2.34%');
  const [volHeat, setVolHeat] = useState(67);
  const [klinePeriod, setKlinePeriod] = useState<KlinePeriod>('1D');
  const [candles, setCandles] = useState(() => genCandles('1D'));
  const orderIdRef = useRef(0);

  // ── Order flow refresh ──
  useEffect(() => {
    const timer = setInterval(() => {
      setOrders((prev) => {
        const next = [generateOrder(), ...prev.slice(0, 7)];
        return next;
      });
      orderIdRef.current += 1;
    }, 1400);
    return () => clearInterval(timer);
  }, []);

  // ── Active signal rotation ──
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSignal((i) => (i + 1) % strategyList.length);
    }, 2200);
    return () => clearInterval(timer);
  }, []);

  // ── HSI price micro-jitter ──
  useEffect(() => {
    const timer = setInterval(() => {
      const delta = (Math.random() - 0.4) * 8;
      setHsiPrice((p) => {
        const np = Math.round((p + delta) * 100) / 100;
        setLastTickUp(delta >= 0);
        return np;
      });
    }, 800);
    return () => clearInterval(timer);
  }, []);

  // ── PnL flicker ──
  useEffect(() => {
    const timer = setInterval(() => {
      const v = (2 + Math.random() * 1.2).toFixed(2);
      setPnlVal(`+${v}%`);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // ── Volatility heat oscillation ──
  useEffect(() => {
    const timer = setInterval(() => {
      setVolHeat((v) => {
        const nv = Math.max(40, Math.min(95, v + Math.floor((Math.random() - 0.5) * 8)));
        return nv;
      });
    }, 900);
    return () => clearInterval(timer);
  }, []);

  // ── Log ticker ──
  useEffect(() => {
    const timer = setInterval(() => {
      setLogIndex((i) => (i + 1) % tradeLogs.length);
    }, 2200);
    return () => clearInterval(timer);
  }, []);

  const handlePeriodSwitch = useCallback((period: KlinePeriod) => {
    setKlinePeriod(period);
    setCandles(genCandles(period));
  }, []);

  const handleSignalClick = useCallback((signalId: string) => {
    setExpandedSignal((prev) => (prev === signalId ? null : signalId));
  }, []);

  const volColor = volHeat > 75 ? '#E05560' : volHeat > 55 ? '#C8A840' : '#48D1B0';
  const volLabel = volHeat > 75 ? 'HIGH' : volHeat > 55 ? 'MOD' : 'LOW';
  const expandedStrategy = expandedSignal ? strategyList.find((s) => s.id === expandedSignal) : null;

  return (
    <div className="terminal-pulse-glow w-full max-w-[620px]">
      <div className="terminal-module p-1.5 md:p-2 backdrop-blur-md">

        {/* ── Terminal Chrome ── */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-background-300/30 mb-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400/60"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-accent-400/60"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/60"></span>
          </div>
          <span className="mono-data text-[9px] text-foreground-600 tracking-widest">ANTCHAIN :: QUANT v3.2.6</span>
          <div className="flex items-center gap-2">
            <span className="text-[9px] mono-data text-emerald-400 font-semibold">LIVE</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
          </div>
        </div>

        {/* ── Live Metrics Row ── */}
        <div className="grid grid-cols-4 gap-2 px-1 mb-3">
          <div className="terminal-module p-2 text-center">
            <span className="block text-[8px] mono-data text-foreground-600 tracking-wider">HSI SPOT</span>
            <span className={`block text-sm mono-data font-bold tabular-nums mt-0.5 ${lastTickUp ? 'text-emerald-400' : 'text-red-400'} data-tick`}>
              {hsiPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="terminal-module p-2 text-center">
            <span className="block text-[8px] mono-data text-foreground-600 tracking-wider">DAILY P&L</span>
            <span className="block text-sm mono-data font-bold text-accent-400 tabular-nums mt-0.5 data-tick">{pnlVal}</span>
          </div>
          <div className="terminal-module p-2 text-center">
            <span className="block text-[8px] mono-data text-foreground-600 tracking-wider">VOL HEAT</span>
            <span className="block text-sm mono-data font-bold tabular-nums mt-0.5" style={{ color: volColor }}>{volHeat}</span>
            <span className="block text-[7px] mono-data text-foreground-600">{volLabel}</span>
          </div>
          <div className="terminal-module p-2 text-center">
            <span className="block text-[8px] mono-data text-foreground-600 tracking-wider">BETA</span>
            <span className="block text-sm mono-data font-bold text-primary-400 tabular-nums mt-0.5">0.84</span>
          </div>
        </div>

        {/* ── Terminal Body: 3-Column Grid ── */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 p-1">

          {/* ── Col 1: K-line + PnL Sparkline + Strategy Signals ── */}
          <div className="col-span-2 space-y-2">

            {/* K-line Chart */}
            <div className="terminal-module p-3 grid-scan">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] mono-data text-foreground-500 font-semibold tracking-wide">HSI</span>
                  {/* ── Period Switcher ── */}
                  <div className="flex items-center gap-0.5 ml-1">
                    {periodLabels.map((p) => (
                      <button
                        key={p.key}
                        onClick={() => handlePeriodSwitch(p.key)}
                        className={`text-[8px] mono-data px-1.5 py-0.5 rounded transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          klinePeriod === p.key
                            ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                            : 'text-foreground-600 hover:text-foreground-400 hover:bg-background-200/50 border border-transparent'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px] mono-data text-foreground-600 tracking-wide">· CANDLE</span>
                </div>
                <span className="text-[10px] mono-data text-emerald-400 font-bold animate-blink">● LIVE</span>
              </div>
              <svg viewBox="0 0 340 140" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
                {/* Grid */}
                {[20, 40, 60, 80, 100, 120].map((y) => (
                  <line key={`g-${y}`} x1="0" y1={y} x2="340" y2={y} stroke="#ffffff" strokeWidth="0.3" strokeOpacity="0.06" strokeDasharray="3 5" />
                ))}
                {/* Candlesticks */}
                {candles.map((base, i) => {
                  const x = 8 + i * 8;
                  const bodyH = 6 + (i % 5);
                  const y = base - bodyH;
                  const isUp = i % 3 !== 1;
                  const topWick = base - (8 + i % 7);
                  const botWick = base + bodyH + (6 + i % 6);
                  const isLast = i === candles.length - 1;
                  return (
                    <g key={i} className={isLast ? 'candle-flash' : ''}>
                      <line x1={x + 2} y1={topWick} x2={x + 2} y2={botWick}
                        stroke={isUp ? '#48D1B0' : '#E05560'} strokeWidth={isLast ? '1.2' : '0.6'}
                        opacity={isLast ? '0.9' : '0.7'} />
                      <rect x={x - 1.5} y={y} width="7" height={bodyH}
                        fill={isUp ? '#48D1B0' : '#E05560'}
                        opacity={isLast ? (isUp ? '0.85' : '0.7') : (isUp ? '0.65' : '0.5')}
                        rx="0.5" />
                      {isLast && (
                        <>
                          <circle cx={x + 2} cy={topWick - 3} r="2" fill="#48D1B0" opacity="0.6">
                            <animate attributeName="opacity" values="0.6;0.1;0.6" dur="1s" repeatCount="indefinite" />
                          </circle>
                          <rect x={x - 4} y={topWick - 8} width="12" height="5" fill="#080c13" opacity="0.9" rx="1" />
                          <text x={x + 2} y={topWick - 4} textAnchor="middle" fill="#48D1B0" fontSize="4" fontFamily="monospace">+1.22%</text>
                        </>
                      )}
                    </g>
                  );
                })}
                {/* MA5 Curve */}
                <path d="M6 80 Q30 65 70 78 Q120 55 170 75 Q220 50 270 72 Q310 58 334 68"
                  fill="none" stroke="#C8A840" strokeWidth="1" strokeOpacity="0.8" />
                {/* MA20 Curve */}
                <path d="M6 88 Q30 75 70 85 Q120 68 170 82 Q220 60 270 80 Q310 65 334 76"
                  fill="none" stroke="#60A8E0" strokeWidth="0.8" strokeOpacity="0.6" strokeDasharray="3 2" />
                {/* Live cursor line */}
                <line x1="334" y1="20" x2="334" y2="125" stroke="#48D1B0" strokeWidth="0.5" strokeOpacity="0.4" strokeDasharray="2 3">
                  <animate attributeName="stroke-opacity" values="0.4;0.1;0.4" dur="1.2s" repeatCount="indefinite" />
                </line>
              </svg>
              <div className="flex items-center justify-between mt-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-px bg-accent-400"></span>
                  <span className="text-[8px] mono-data text-foreground-600">MA5</span>
                  <span className="w-2.5 h-px inline-block" style={{ borderTop: '0.8px dashed #60A8E0' }}></span>
                  <span className="text-[8px] mono-data text-foreground-600">MA20</span>
                </div>
                <span className="text-[8px] mono-data text-foreground-600">VOL 1.2B · OI 4.8M</span>
              </div>
            </div>

            {/* P&L Sparkline */}
            <div className="terminal-module p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] mono-data text-foreground-500 font-semibold tracking-wide">REALIZED P&L · 50D</span>
                <span className="text-[10px] mono-data text-accent-400 font-bold">+$12.4M YTD</span>
              </div>
              <svg viewBox="0 0 340 60" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#48D1B0" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#48D1B0" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d={`M0 60 L0 ${60 - pnlPoints[0] * 0.5} ${pnlPoints.map((p, i) => `L${(i / (pnlPoints.length - 1)) * 340} ${60 - p * 0.5}`).join(' ')} L340 60 Z`}
                  fill="url(#pnlGrad)"
                />
                <path
                  d={`M0 ${60 - pnlPoints[0] * 0.5} ${pnlPoints.map((p, i) => `L${(i / (pnlPoints.length - 1)) * 340} ${60 - p * 0.5}`).join(' ')}`}
                  fill="none"
                  stroke="#48D1B0"
                  strokeWidth="1.2"
                  strokeOpacity="0.85"
                  className="sparkline-path"
                />
                <circle cx="340" cy={60 - pnlPoints[pnlPoints.length - 1] * 0.5} r="2.5" fill="#48D1B0" opacity="0.9">
                  <animate attributeName="r" values="2.5;4;2.5" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.9;0.4;0.9" dur="1.5s" repeatCount="indefinite" />
                </circle>
                {[0, 12, 25, 37, 49].map((idx) => {
                  const p = pnlPoints[idx];
                  const x = (idx / (pnlPoints.length - 1)) * 340;
                  const y = 60 - p * 0.5;
                  return <circle key={idx} cx={x} cy={y} r="1.5" fill="#48D1B0" opacity="0.5" />;
                })}
              </svg>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[8px] mono-data text-foreground-600">T-50</span>
                <span className="text-[8px] mono-data text-foreground-600">TODAY</span>
              </div>
            </div>

            {/* Strategy Signals */}
            <div className="terminal-module p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] mono-data text-foreground-500 font-semibold tracking-wide">STRATEGY SIGNALS</span>
                <span className="text-[9px] mono-data text-foreground-600">6 ACTIVE · 3 QUEUE</span>
              </div>
              <div className="space-y-1">
                {strategyList.map((s, i) => {
                  const isActive = i === activeSignal;
                  const isExpanded = expandedSignal === s.id;
                  return (
                    <div key={s.id}>
                      <div
                        onClick={() => handleSignalClick(s.id)}
                        className={`flex items-center justify-between text-[9px] px-2 py-1 rounded transition-all duration-300 cursor-pointer ${
                          isActive ? 'signal-active' : ''
                        } ${isExpanded ? 'bg-accent-500/8 border border-accent-500/20' : 'hover:bg-background-200/30'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="mono-data text-primary-400 font-semibold w-7">{s.id}</span>
                          <span className="text-foreground-400">{s.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="mono-data text-[8px] text-foreground-600 w-10 text-right">{s.freq}</span>
                          <span className={`mono-data font-bold w-12 text-right ${
                            s.status === 'green' ? 'text-emerald-400' :
                            s.status === 'yellow' ? 'text-accent-400' :
                            'text-red-400'
                          }`}>
                            {s.signal}
                          </span>
                          <span className="text-[7px] text-foreground-600 w-3 text-right">
                            {isExpanded ? <i className="ri-arrow-up-s-line text-accent-400"></i> : <i className="ri-arrow-down-s-line"></i>}
                          </span>
                        </div>
                      </div>

                      {/* ── Expanded Signal Detail ── */}
                      {isExpanded && (
                        <div className="mt-1 mb-1.5 mx-1 p-2.5 rounded bg-background-200/40 border border-accent-500/15 grid grid-cols-3 gap-2 animate-fade-in">
                          <div className="col-span-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="mono-data text-accent-400 font-bold text-[10px]">{s.id} · {s.name}</span>
                              <span className={`text-[8px] mono-data font-bold px-1.5 py-0.5 rounded ${
                                s.status === 'green' ? 'bg-emerald-400/15 text-emerald-400' :
                                s.status === 'yellow' ? 'bg-accent-400/15 text-accent-400' :
                                'bg-red-400/15 text-red-400'
                              }`}>{s.signal}</span>
                            </div>
                            <span className="mono-data text-[8px] text-foreground-600">响应频率 {s.freq}</span>
                          </div>
                          {/* Confidence bar */}
                          <div className="col-span-3">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-[8px] mono-data text-foreground-600">CONFIDENCE</span>
                              <span className="text-[9px] mono-data font-bold text-emerald-400">{s.confidence}%</span>
                            </div>
                            <div className="w-full h-1.5 rounded-full bg-background-300/40 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                  width: `${s.confidence}%`,
                                  background: s.confidence > 80 ? 'linear-gradient(90deg, #48D1B0, #3AB090)' :
                                    s.confidence > 50 ? 'linear-gradient(90deg, #C8A840, #A88830)' :
                                    'linear-gradient(90deg, #E05560, #C04050)',
                                  animation: 'detailBarGrow 0.6s ease-out forwards',
                                }}
                              ></div>
                            </div>
                          </div>
                          <div>
                            <span className="text-[7px] mono-data text-foreground-600 block">ALLOC</span>
                            <span className="text-[9px] mono-data font-bold text-foreground-200">{s.allocation}</span>
                          </div>
                          <div>
                            <span className="text-[7px] mono-data text-foreground-600 block">DAILY P&L</span>
                            <span className={`text-[9px] mono-data font-bold ${s.dailyPnL.startsWith('+') ? 'text-emerald-400' : 'text-red-400'}`}>{s.dailyPnL}</span>
                          </div>
                          <div>
                            <span className="text-[7px] mono-data text-foreground-600 block">MONTHLY</span>
                            <span className={`text-[9px] mono-data font-bold ${s.monthlyPnL.startsWith('+') ? 'text-emerald-400' : 'text-red-400'}`}>{s.monthlyPnL}</span>
                          </div>
                          <div>
                            <span className="text-[7px] mono-data text-foreground-600 block">RISK CTRB</span>
                            <span className="text-[9px] mono-data font-bold text-foreground-200">{s.riskContrib}</span>
                          </div>
                          <div>
                            <span className="text-[7px] mono-data text-foreground-600 block">SHARPE</span>
                            <span className="text-[9px] mono-data font-bold text-accent-400">{s.sharpe}</span>
                          </div>
                          <div>
                            <span className="text-[7px] mono-data text-foreground-600 block">HORIZON</span>
                            <span className="text-[9px] mono-data font-bold text-foreground-200">{s.signal === 'LONG' || s.signal === 'ENTRY' ? '3-6M' : s.signal === 'HOLD' ? '1-3M' : 'N/A'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Col 2: Risk Radar + Asset Ring + Order Flow ── */}
          <div className="space-y-2">

            {/* Risk Radar */}
            <div className="terminal-module p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] mono-data text-foreground-500 font-semibold tracking-wide">RISK RADAR</span>
                <span className="text-[9px] mono-data text-emerald-400">SAFE</span>
              </div>
              <svg viewBox="0 0 100 100" className="w-full h-auto mt-1" xmlns="http://www.w3.org/2000/svg">
                {[20, 35, 45].map((r) => (
                  <circle key={r} cx="50" cy="48" r={r} fill="none" stroke="#ffffff" strokeWidth="0.3" strokeOpacity="0.08" />
                ))}
                {[0, 72, 144, 216, 288].map((angle, i) => (
                  <line key={i} x1="50" y1="48"
                    x2={50 + 45 * Math.cos(angle * Math.PI / 180)}
                    y2={48 + 45 * Math.sin(angle * Math.PI / 180)}
                    stroke="#ffffff" strokeWidth="0.3" strokeOpacity="0.1" />
                ))}
                <polygon points="50,8 70,28 78,48 62,70 38,70 22,48 30,28"
                  fill="#48D1B0" fillOpacity="0.08" stroke="#48D1B0" strokeWidth="0.8" strokeOpacity="0.5" />
                <polygon points="50,20 58,34 60,48 52,58 48,58 40,48 42,34"
                  fill="#C8A840" fillOpacity="0.06" stroke="#C8A840" strokeWidth="0.6" strokeOpacity="0.4" strokeDasharray="2 1" />
                <line x1="50" y1="48" x2="50" y2="3"
                  stroke="#48D1B0" strokeWidth="0.8" strokeOpacity="0.6"
                  className="radar-sweep" style={{ transformOrigin: '50px 48px' }} />
                <path d="M50 48 L50 3 A45 45 0 0 1 95 48 Z"
                  fill="url(#sweepGrad)" opacity="0.15" className="radar-sweep" style={{ transformOrigin: '50px 48px' }} />
                <defs>
                  <linearGradient id="sweepGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#48D1B0" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#48D1B0" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <text x="50" y="5" textAnchor="middle" fill="#ffffff" fontSize="3.5" opacity="0.5" fontFamily="monospace">MKT</text>
                <text x="92" y="30" textAnchor="middle" fill="#ffffff" fontSize="3.5" opacity="0.5" fontFamily="monospace">CRED</text>
                <text x="82" y="72" textAnchor="middle" fill="#ffffff" fontSize="3.5" opacity="0.5" fontFamily="monospace">LIQ</text>
                <text x="18" y="72" textAnchor="middle" fill="#ffffff" fontSize="3.5" opacity="0.5" fontFamily="monospace">OPS</text>
                <text x="8" y="30" textAnchor="middle" fill="#ffffff" fontSize="3.5" opacity="0.5" fontFamily="monospace">MOD</text>
              </svg>
              <div className="flex items-center gap-3 mt-1 text-[8px]">
                <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span><span className="mono-data text-foreground-600">当前</span></div>
                <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span><span className="mono-data text-foreground-600">基准</span></div>
              </div>
            </div>

            {/* Asset Allocation Ring */}
            <div className="terminal-module p-3">
              <span className="text-[10px] mono-data text-foreground-500 font-semibold tracking-wide">ALLOCATION</span>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="w-14 h-14 relative flex-shrink-0">
                  <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="24" fill="none" stroke="#ffffff" strokeWidth="8" strokeOpacity="0.06" />
                    <circle cx="32" cy="32" r="24" fill="none" stroke="#60A8E0" strokeWidth="8"
                      strokeDasharray="55 95" strokeOpacity="0.7" className="ring-animate" />
                    <circle cx="32" cy="32" r="24" fill="none" stroke="#48D1B0" strokeWidth="8"
                      strokeDasharray="35 115" strokeOpacity="0.7" strokeDashoffset="-55" className="ring-animate" style={{ animationDelay: '0.4s' }} />
                    <circle cx="32" cy="32" r="24" fill="none" stroke="#C8A840" strokeWidth="8"
                      strokeDasharray="28 122" strokeOpacity="0.7" strokeDashoffset="-90" className="ring-animate" style={{ animationDelay: '0.8s' }} />
                    <circle cx="32" cy="32" r="24" fill="none" stroke="#8844CC" strokeWidth="8"
                      strokeDasharray="32 118" strokeOpacity="0.5" strokeDashoffset="-118" className="ring-animate" style={{ animationDelay: '1.2s' }} />
                    <circle cx="32" cy="32" r="4" fill="#48D1B0" opacity="0.3">
                      <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>
                <div className="space-y-0.5 text-[8px]">
                  {[
                    { label: '权益', pct: '37%', color: '#60A8E0' },
                    { label: '固收', pct: '23%', color: '#48D1B0' },
                    { label: '另类', pct: '19%', color: '#C8A840' },
                    { label: '现金', pct: '21%', color: '#8844CC' },
                  ].map((a) => (
                    <div key={a.label} className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: a.color }}></span>
                      <span className="text-foreground-500">{a.label}</span>
                      <span className="mono-data text-foreground-300 font-semibold ml-auto">{a.pct}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Order Flow / Blotter */}
            <div className="terminal-module p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] mono-data text-foreground-500 font-semibold tracking-wide">ORDER FLOW</span>
                <span className="text-[9px] mono-data text-emerald-400 animate-blink">●</span>
              </div>
              <div className="space-y-0.5">
                {orders.map((o, i) => (
                  <div
                    key={`${orderIdRef.current}-${i}`}
                    className={`flex items-center gap-1 text-[9px] ${i === 0 ? 'order-flow-enter' : ''}`}
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    <span className="mono-data text-foreground-600 w-[52px] shrink-0">{o.time}</span>
                    <span className={`mono-data font-bold w-3 shrink-0 ${o.side === 'B' ? 'text-emerald-400' : 'text-red-400'}`}>{o.side}</span>
                    <span className="mono-data text-foreground-400 tabular-nums w-10 text-right shrink-0">{o.qty}</span>
                    <span className={`mono-data tabular-nums ml-auto ${o.aggr ? 'text-accent-400 font-semibold' : 'text-foreground-500'}`}>{o.px}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2 text-[8px]">
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                <span className="text-foreground-600 mono-data">Streaming · 1,247 msg/s</span>
                <span className="text-foreground-600 mono-data ml-auto">Depth 42</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom Trade Log Ticker ── */}
        <div className="mt-2 mx-1 px-3 py-2 terminal-module overflow-hidden">
          <div className="flex items-center gap-2">
            <span className="text-[8px] mono-data text-foreground-600 uppercase tracking-wider shrink-0">SYS LOG</span>
            <div className="log-scroll-container flex-1 overflow-hidden h-4 relative">
              <div className="absolute inset-0 flex items-center">
                <span className="text-[9px] mono-data text-foreground-400 animate-fade-in">
                  {tradeLogs[logIndex]}
                </span>
              </div>
            </div>
            <span className="text-[8px] mono-data text-accent-400 tabular-nums shrink-0">{new Date().toLocaleTimeString('en-US', { hour12: false })}</span>
          </div>
        </div>

        {/* ── Bottom Status Bar ── */}
        <div className="flex items-center justify-between px-3 py-2 border-t border-background-300/30 mt-2">
          <div className="flex items-center gap-3">
            <span className="text-[8px] mono-data text-foreground-600 uppercase">CONN: <span className="text-emerald-400">SECURE</span></span>
            <span className="text-[8px] mono-data text-foreground-600 uppercase">LAT: <span className="text-foreground-400">4ms</span></span>
            <span className="text-[8px] mono-data text-foreground-600 uppercase">UPT: <span className="text-foreground-400">99.99%</span></span>
            <span className="text-[8px] mono-data text-foreground-600 uppercase">CPU: <span className="text-foreground-400">12%</span></span>
          </div>
          <span className="text-[8px] mono-data text-foreground-600 animate-blink">█</span>
        </div>
      </div>
    </div>
  );
}